import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2, 
  Package, 
  List, 
  Settings,
  FileText,
  Beaker,
  CheckCircle,
  XCircle,
  Clock,
  Archive,
  Copy,
  ThermometerSun,
  Camera,
  Scale,
  GripVertical,
  AlertTriangle,
  Loader2,
  Search
} from 'lucide-react';
import { insumoCorrespondeTermo } from '../utils/insumoBusca';
import {
  TEMPO_UNIDADES,
  normalizeTempoUnidade,
  labelTempoUnidade
} from '../utils/tempoUnidade';

/** Exibe quantidade no input com vírgula decimal (pt-BR). Vazio se não houver valor. */
function qtyToCommaInput(v) {
  if (v === '' || v == null) return '';
  if (typeof v === 'string') return v;
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return String(n).replace('.', ',');
}

/** Interpreta string digitada: vírgula como decimal (pt-BR); se não houver vírgula, aceita ponto decimal. */
function parseDecimalComma(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return NaN;
  if (s.includes(',')) {
    const normalized = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : NaN;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

/** Normaliza um parâmetro de qualidade para a API. */
function normalizeParametroParaApi(p) {
  if (p?.tipo === 'range') {
    const nMin = parseDecimalComma(p.min);
    const nMax = parseDecimalComma(p.max);
    return {
      nome: p.nome || '',
      tipo: 'range',
      obrigatorio: p.obrigatorio !== false,
      critico: !!p.critico,
      unidade: p.unidade || '',
      min: Number.isFinite(nMin) ? nMin : null,
      max: Number.isFinite(nMax) ? nMax : null
    };
  }
  return { ...(p || {}) };
}

/** Remove _localKey e converte parâmetros (faixa) e validações para números na API. */
function normalizeProcessoParaApi(processoRows) {
  return processoRows.map(({ _localKey, ...etapa }) => {
    const parametros = (etapa.parametros || []).map(normalizeParametroParaApi);
    const validacoes = (etapa.validacoes || [])
      .map((v) => {
        const vmin = parseDecimalComma(v.valor_minimo ?? v.valor_min);
        const vmax = parseDecimalComma(v.valor_maximo ?? v.valor_max);
        return {
          nome: v.nome || '',
          tipo: v.tipo || '',
          valor_minimo: Number.isFinite(vmin) ? vmin : null,
          valor_maximo: Number.isFinite(vmax) ? vmax : null
        };
      })
      .filter((v) => v.nome?.trim() || Number.isFinite(v.valor_minimo) || Number.isFinite(v.valor_maximo));
    const {
      fotos_etapa_ativo,
      fotos_etapa: fotosEtapaUi,
      peso_final_ativo,
      ciclos_ativo: ciclosAtivoUi,
      ciclos: ciclosUi,
      ...etapaRest
    } = etapa;
    const fotos_etapa =
      fotos_etapa_ativo && Array.isArray(fotosEtapaUi)
        ? fotosEtapaUi
            .map((f) => {
              const nome = String(f.nome || '').trim();
              if (!nome) return null;
              const id =
                f.id ||
                (typeof crypto !== 'undefined' && crypto.randomUUID
                  ? crypto.randomUUID()
                  : `foto-${Date.now()}-${Math.random()}`);
              return {
                id,
                nome,
                instrucoes: String(f.instrucoes || '').trim()
              };
            })
            .filter(Boolean)
        : [];
    const ciclos_ativo = !!ciclosAtivoUi;
    const ciclos = ciclos_ativo
      ? (Array.isArray(ciclosUi) ? ciclosUi : []).map((c, i) => {
          const tempo =
            c.tempo_estimado != null && c.tempo_estimado !== ''
              ? parseInt(String(c.tempo_estimado), 10)
              : null;
          return {
            id:
              c.id ||
              (typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `ciclo-${Date.now()}-${i}`),
            ordem: i + 1,
            tempo_estimado: Number.isFinite(tempo) && tempo > 0 ? tempo : null,
            tempo_unidade: normalizeTempoUnidade(c.tempo_unidade),
            parametros: (c.parametros || []).map(normalizeParametroParaApi),
            fotos_ciclo: (Array.isArray(c.fotos_ciclo) ? c.fotos_ciclo : [])
              .map((f) => {
                const nome = String(f?.nome || '').trim();
                if (!nome) return null;
                const id =
                  f.id ||
                  (typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `foto-ciclo-${Date.now()}-${Math.random()}`);
                return {
                  id,
                  nome,
                  instrucoes: String(f.instrucoes || '').trim()
                };
              })
              .filter(Boolean)
          };
        })
      : [];
    const embalagens_permitidas = isEtapaEmbalagemOuEncaixotamento(etapa.nome)
      ? normalizeEmbalagensPermitidas(etapa.embalagens_permitidas)
      : [];
    return {
      ...etapaRest,
      parametros,
      validacoes,
      fotos_etapa,
      peso_final_ativo: !!peso_final_ativo,
      ciclos_ativo,
      ciclos,
      embalagens_permitidas,
      // Com ciclos, o tempo fica em cada ciclo — não na etapa
      tempo_estimado: ciclos_ativo ? null : etapa.tempo_estimado ?? null,
      tempo_unidade: ciclos_ativo
        ? null
        : normalizeTempoUnidade(etapa.tempo_unidade)
    };
  });
}

function newModalLocalKey(prefix = 'k') {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${prefix}-${Date.now()}-${Math.random()}`;
}

const ETAPA_ANCORA_SEPARACAO = 'Separação e Pesagem';
const ETAPA_ANCORA_EMBALAGEM = 'Embalagem';
const ETAPA_ANCORA_ENCAIXOTAMENTO = 'Encaixotamento';

function isEtapaEmbalagemOuEncaixotamento(nome) {
  const n = String(nome || '').trim();
  return n === ETAPA_ANCORA_EMBALAGEM || n === ETAPA_ANCORA_ENCAIXOTAMENTO;
}

function isInsumoEmbalagem(p) {
  return String(p?.tipo_insumo || '') === 'embalagem';
}

function normalizeEmbalagensPermitidas(lista) {
  if (!Array.isArray(lista)) return [];
  return lista
    .map((e) => {
      const id = String(e?.id || e?.embalagem_id || '').trim();
      if (!id) return null;
      return {
        id,
        nome: String(e?.nome || e?.embalagem_nome || '').trim(),
        codigo: String(e?.codigo || e?.embalagem_codigo || '').trim()
      };
    })
    .filter(Boolean);
}

/** Primeira = Separação; penúltima = Embalagem; última = Encaixotamento; intermediárias entre separação e embalagem. */
function createDefaultProcessoAncoras() {
  return [
    {
      _localKey: newModalLocalKey('etapa'),
      ordem: 1,
      nome: ETAPA_ANCORA_SEPARACAO,
      descricao: 'Separação e pesagem dos ingredientes para montagem do carrinho.',
      tipo: 'processo',
      tempo_estimado: null,
      tempo_unidade: 'min',
      parametros: [],
      validacoes: [],
      fotos_etapa_ativo: false,
      fotos_etapa: [],
      peso_final_ativo: false,
      ciclos_ativo: false,
      ciclos: [],
      embalagens_permitidas: []
    },
    {
      _localKey: newModalLocalKey('etapa'),
      ordem: 2,
      nome: ETAPA_ANCORA_EMBALAGEM,
      descricao: 'Embalagem do produto acabado.',
      tipo: 'processo',
      tempo_estimado: null,
      tempo_unidade: 'min',
      parametros: [],
      validacoes: [],
      fotos_etapa_ativo: false,
      fotos_etapa: [],
      peso_final_ativo: false,
      ciclos_ativo: false,
      ciclos: [],
      embalagens_permitidas: []
    },
    {
      _localKey: newModalLocalKey('etapa'),
      ordem: 3,
      nome: ETAPA_ANCORA_ENCAIXOTAMENTO,
      descricao: 'Encaixotamento e paletização do produto acabado.',
      tipo: 'processo',
      tempo_estimado: null,
      tempo_unidade: 'min',
      parametros: [],
      validacoes: [],
      fotos_etapa_ativo: false,
      fotos_etapa: [],
      peso_final_ativo: false,
      ciclos_ativo: false,
      ciclos: [],
      embalagens_permitidas: []
    }
  ];
}

/** Cadastros legados (apenas Separação + Embalagem): acrescenta Encaixotamento ao carregar. */
function appendEncaixotamentoIfLegacy(rawProcesso) {
  const arr = Array.isArray(rawProcesso) ? [...rawProcesso] : [];
  if (arr.length !== 2) return arr;
  const n0 = String(arr[0]?.nome || '')
    .trim()
    .replace(/\s+/g, ' ');
  const n1 = String(arr[1]?.nome || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (n0 === ETAPA_ANCORA_SEPARACAO && n1 === ETAPA_ANCORA_EMBALAGEM) {
    arr.push({
      _localKey: newModalLocalKey('etapa'),
      ordem: 3,
      nome: ETAPA_ANCORA_ENCAIXOTAMENTO,
      descricao: 'Encaixotamento e paletização do produto acabado.',
      tipo: 'processo',
      tempo_estimado: null,
      parametros: [],
      validacoes: [],
      fotos_etapa_ativo: false,
      fotos_etapa: [],
      peso_final_ativo: false,
      ciclos_ativo: false,
      ciclos: [],
      embalagens_permitidas: []
    });
    arr.forEach((e, i) => {
      e.ordem = i + 1;
    });
  }
  return arr;
}

/**
 * Converte produto da API para estado do modal (vírgula decimal, chaves locais de etapas/fotos).
 * @param {object|null|undefined} product
 * @param {{ cloneForNew?: boolean }} opts cloneForNew: remove id das etapas e renova chaves (novo produto a partir de modelo).
 */
function mapProductToModalForm(product, opts = {}) {
  const cloneForNew = !!opts.cloneForNew;
  if (!product) {
    return {
      nomeProduto: '',
      codigo: '',
      status: 'rascunho',
      formulacao: [],
      processo: createDefaultProcessoAncoras()
    };
  }

  const nomeBase = (product.nome_produto || '').trim();
  const nomeProduto = cloneForNew ? (nomeBase ? `${nomeBase} (novo)` : '') : nomeBase;
  const codigo = cloneForNew ? '' : (product.codigo || '').trim();
  const status = cloneForNew ? 'rascunho' : product.status || 'rascunho';

  const formulacao = (product.formulacao || []).map((row) => ({
    ...row,
    quantidade_minima: qtyToCommaInput(row.quantidade_minima),
    quantidade_maxima: qtyToCommaInput(row.quantidade_maxima)
  }));

  let rawProcesso = product.processo_fabricacao || [];
  if (!cloneForNew && (!rawProcesso || rawProcesso.length === 0)) {
    rawProcesso = createDefaultProcessoAncoras();
  }
  rawProcesso = appendEncaixotamentoIfLegacy(rawProcesso);
  const processo = rawProcesso.map((e) => {
    const etapaSource =
      cloneForNew && e && typeof e === 'object'
        ? Object.fromEntries(Object.entries(e).filter(([k]) => k !== 'id'))
        : { ...e };

    return {
      ...etapaSource,
      cardinalidade: e.cardinalidade || '1-1',
      _localKey:
        !cloneForNew && e._localKey
          ? e._localKey
          : newModalLocalKey('etapa'),
      parametros: (e.parametros || []).map((p) =>
        p.tipo === 'range'
          ? { ...p, min: qtyToCommaInput(p.min), max: qtyToCommaInput(p.max) }
          : { ...p }
      ),
      validacoes: (e.validacoes || []).map((v) => ({
        nome: v.nome || '',
        tipo: v.tipo || '',
        valor_minimo:
          v.valor_minimo != null && v.valor_minimo !== ''
            ? qtyToCommaInput(v.valor_minimo)
            : v.valor_esperado != null && v.valor_esperado !== ''
            ? qtyToCommaInput(v.valor_esperado)
            : '',
        valor_maximo:
          v.valor_maximo != null && v.valor_maximo !== '' ? qtyToCommaInput(v.valor_maximo) : ''
      })),
      fotos_etapa: (e.fotos_etapa || []).map((f) => {
        const fSrc =
          cloneForNew && f && typeof f === 'object'
            ? Object.fromEntries(
                Object.entries(f).filter(([k]) => !['id', '_localKeyFoto'].includes(k))
              )
            : { ...f };
        return {
          ...fSrc,
          _localKeyFoto:
            !cloneForNew && f._localKeyFoto ? f._localKeyFoto : newModalLocalKey('foto'),
          nome: f.nome || '',
          instrucoes: f.instrucoes || ''
        };
      }),
      fotos_etapa_ativo: !!(e.fotos_etapa && e.fotos_etapa.length) || !!e.fotos_etapa_ativo,
      peso_final_ativo: !!e.peso_final_ativo,
      ciclos_ativo: !!e.ciclos_ativo,
      ciclos: (e.ciclos || []).map((c) => ({
        ...c,
        _localKeyCiclo: c._localKeyCiclo || newModalLocalKey('ciclo'),
        tempo_unidade: normalizeTempoUnidade(c.tempo_unidade),
        parametros: (c.parametros || []).map((p) =>
          p.tipo === 'range'
            ? { ...p, min: qtyToCommaInput(p.min), max: qtyToCommaInput(p.max) }
            : { ...p }
        ),
        fotos_ciclo: (c.fotos_ciclo || []).map((f) => {
          const fSrc =
            cloneForNew && f && typeof f === 'object'
              ? Object.fromEntries(
                  Object.entries(f).filter(([k]) => !['id', '_localKeyFoto'].includes(k))
                )
              : { ...f };
          return {
            ...fSrc,
            _localKeyFoto:
              !cloneForNew && f._localKeyFoto ? f._localKeyFoto : newModalLocalKey('foto'),
            nome: f.nome || '',
            instrucoes: f.instrucoes || ''
          };
        })
      })),
      tempo_unidade: normalizeTempoUnidade(e.tempo_unidade),
      embalagens_permitidas: normalizeEmbalagensPermitidas(e.embalagens_permitidas)
    };
  });

  return { nomeProduto, codigo, status, formulacao, processo };
}

const ProdutosFinaisPage = () => {
  const { hasPermission, getEmpresaId } = useAuth();
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit, view
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('formulacao'); // formulacao, processo
  
  // Stats
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (hasPermission('view_products')) {
      loadProducts();
      loadStats();
    }
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const listProductionProducts = httpsCallable(functions, 'listProductionProducts');
      const result = await listProductionProducts({
        empresa_id: getEmpresaId(),
        page: 1,
        limit: 100
      });

      if (result.data.success) {
        setProdutos(result.data.data.items);
      } else {
        setError('Erro ao carregar produtos');
      }
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const getProductionProductsStats = httpsCallable(functions, 'getProductionProductsStats');
      const result = await getProductionProductsStats({
        empresa_id: getEmpresaId()
      });

      if (result.data.success) {
        setStats(result.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setModalMode('create');
    setActiveTab('formulacao');
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setModalMode('edit');
    setActiveTab('formulacao');
    setShowModal(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setModalMode('view');
    setActiveTab('formulacao');
    setShowModal(true);
  };

  const handleDeleteProduct = async (productId, productStatus = 'rascunho') => {
    const msg =
      productStatus === 'ativo'
        ? 'Tem certeza que deseja excluir este produto ATIVO? Ele sairá do catálogo e a ação não pode ser desfeita.'
        : 'Tem certeza que deseja excluir este produto?';
    if (!window.confirm(msg)) {
      return;
    }

    try {
      const deleteProductionProduct = httpsCallable(functions, 'deleteProductionProduct');
      const result = await deleteProductionProduct({ product_id: productId });

      if (result.data.success) {
        setSuccess('Produto excluído com sucesso!');
        setTimeout(() => setSuccess(''), 3000);
        loadProducts();
        loadStats();
      } else {
        setError('Erro ao excluir produto');
      }
    } catch (err) {
      console.error('Erro ao excluir produto:', err);
      const msgErr =
        err?.details ||
        err?.message?.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, '') ||
        'Erro ao excluir produto';
      setError(msgErr);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      ativo: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Ativo' },
      rascunho: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Rascunho' },
      arquivado: { color: 'bg-gray-100 text-gray-800', icon: Archive, label: 'Arquivado' }
    };

    const badge = badges[status] || badges.rascunho;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  if (!hasPermission('view_products')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso Negado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Você não tem permissão para visualizar produtos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produtos</h1>
          <p className="text-gray-600">Gerencie formulações e processos de fabricação</p>
        </div>
        
        {hasPermission('manage_products') && (
          <button
            onClick={handleCreateProduct}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Produto
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Produtos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <Package className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ativos</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.ativos}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rascunhos</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.rascunhos}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Arquivados</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">{stats.arquivados}</p>
              </div>
              <Archive className="h-8 w-8 text-gray-600" />
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Products List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2">Carregando produtos...</span>
        </div>
      ) : produtos.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum produto cadastrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Comece criando seu primeiro produto.
          </p>
          {hasPermission('manage_products') && (
            <button
              onClick={handleCreateProduct}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Criar Primeiro Produto
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Versão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ingredientes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Etapas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {produtos.map((produto) => (
                <tr key={produto.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Package className="h-8 w-8 text-gray-400" />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {produto.nome_produto}
                        </div>
                        <div className="text-sm text-gray-500">
                          {produto.codigo ? (
                            <span className="font-mono">Cód.: {produto.codigo}</span>
                          ) : (
                            <span>{produto.formulacao?.length || 0} ingredientes</span>
                          )}
                          {produto.codigo ? (
                            <span className="ml-2">· {produto.formulacao?.length || 0} ingredientes</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono text-gray-900">{produto.versao}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(produto.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Beaker className="h-4 w-4 mr-1 text-blue-600" />
                      {produto.formulacao?.length || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <List className="h-4 w-4 mr-1 text-green-600" />
                      {produto.processo_fabricacao?.length || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewProduct(produto)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      
                      {hasPermission('manage_products') && (
                        <>
                          <button
                            onClick={() => handleEditProduct(produto)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          
                          {(produto.status === 'rascunho' || produto.status === 'ativo' || produto.status === 'arquivado') && (
                            <button
                              onClick={() => handleDeleteProduct(produto.id, produto.status)}
                              className="text-red-600 hover:text-red-900"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <ProductModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedProduct(null);
            setError('');
            setSuccess('');
          }}
          mode={modalMode}
          product={selectedProduct}
          allProducts={produtos}
          onSave={() => {
            setShowModal(false);
            setSelectedProduct(null);
            loadProducts();
            loadStats();
            setSuccess('Produto salvo com sucesso!');
            setTimeout(() => setSuccess(''), 3000);
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </div>
  );
};

// ==================== MODAL: ESCOLHER MATÉRIA-PRIMA (busca + filtros) ====================

const getMpDisplayName = (mp) => mp?.nome || mp?.nome_produto || '—';
const getMpCodigo = (mp) => mp?.codigo || mp?.codigo_produto || mp?.codigo_origem || '';

const MateriaPrimaPickerModal = ({
  isOpen,
  onClose,
  materiasPrimas,
  loadingMaterias,
  onConfirm,
  excludeIds = []
}) => {
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setCategoria('');
      setSelectedId(null);
    }
  }, [isOpen]);

  const categorias = useMemo(() => {
    const s = new Set();
    (materiasPrimas || []).forEach((mp) => {
      if (mp.categoria) s.add(mp.categoria);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [materiasPrimas]);

  const filtered = useMemo(() => {
    let list = [...(materiasPrimas || [])];
    if (excludeIds.length) {
      list = list.filter((mp) => mp.id && !excludeIds.includes(mp.id));
    }
    const term = search.trim();
    if (term) {
      list = list.filter((mp) =>
        insumoCorrespondeTermo(mp, term, {
          getNome: getMpDisplayName,
          getCodigo: getMpCodigo,
        })
      );
    }
    if (categoria) {
      list = list.filter((mp) => mp.categoria === categoria);
    }
    list.sort((a, b) => getMpDisplayName(a).localeCompare(getMpDisplayName(b), 'pt-BR'));
    return list;
  }, [materiasPrimas, search, categoria, excludeIds]);

  const handleConfirm = () => {
    if (!selectedId) return;
    const mp = (materiasPrimas || []).find((m) => m.id === selectedId);
    if (mp) {
      onConfirm(mp);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        role="dialog"
        aria-labelledby="mp-picker-title"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 id="mp-picker-title" className="text-lg font-semibold text-gray-900">
            Escolher ingrediente
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Fechar"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 border-b space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, sinônimo, código ou subcategoria..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-sm text-gray-600">Categoria:</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm min-w-[180px]"
            >
              <option value="">Todas</option>
              {categorias.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500 ml-auto">
              {loadingMaterias ? 'Carregando…' : `${filtered.length} resultado(s)`}
            </span>
          </div>
        </div>

        <div className="flex-1 min-h-[200px] max-h-[45vh] overflow-y-auto px-2">
          {loadingMaterias ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin mr-2" />
              Carregando ingredientes…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              Nenhum ingrediente encontrado. Ajuste a busca ou a categoria.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((mp) => {
                const active = selectedId === mp.id;
                return (
                  <li key={mp.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(mp.id)}
                      onDoubleClick={() => {
                        setSelectedId(mp.id);
                        onConfirm(mp);
                        onClose();
                      }}
                      className={`w-full text-left px-3 py-2.5 rounded-md my-0.5 transition-colors ${
                        active ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{getMpDisplayName(mp)}</div>
                      <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                        {getMpCodigo(mp) && <span>Código: {getMpCodigo(mp)}</span>}
                        {mp.categoria && <span>Categoria: {mp.categoria}</span>}
                        {mp.subcategoria && <span>{mp.subcategoria}</span>}
                        {mp.unidade_medida && <span>Unid.: {mp.unidade_medida}</span>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-3 border-t flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedId || loadingMaterias}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Usar este ingrediente
          </button>
        </div>
        <p className="px-4 pb-3 text-xs text-gray-400 text-center">
          Dica: clique duas vezes em um item para confirmar na hora.
        </p>
      </div>
    </div>
  );
};

// ==================== MODAL DO PRODUTO ====================

const ProductModal = ({ isOpen, onClose, mode, product, allProducts = [], onSave, activeTab, setActiveTab }) => {
  const { getEmpresaId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMpPicker, setShowMpPicker] = useState(false);
  const [mpPickerEditIndex, setMpPickerEditIndex] = useState(null);
  const [templateSourceId, setTemplateSourceId] = useState('');
  
  // Dados do produto
  const [nomeProduto, setNomeProduto] = useState(product?.nome_produto || '');
  const [codigo, setCodigo] = useState(product?.codigo || '');
  const [status, setStatus] = useState(product?.status || 'rascunho');
  
  // Formulação
  const [formulacao, setFormulacao] = useState(product?.formulacao || []);
  const [materiasPrimas, setMateriasPrimas] = useState([]);
  const [loadingMaterias, setLoadingMaterias] = useState(false);
  
  // Processo
  const [processo, setProcesso] = useState(product?.processo_fabricacao || []);
  const [tiposEquipamento, setTiposEquipamento] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadMateriasPrimas();
      (async () => {
        try {
          const fn = httpsCallable(functions, 'listTiposEquipamento');
          const r = await fn({ apenas_ativos: true });
          setTiposEquipamento(r.data?.data?.items || []);
        } catch (e) {
          console.error('Erro ao carregar tipos de equipamento:', e);
        }
      })();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const mapped = mapProductToModalForm(product, { cloneForNew: false });
    setNomeProduto(mapped.nomeProduto);
    setCodigo(mapped.codigo);
    setStatus(mapped.status);
    setFormulacao(mapped.formulacao);
    setProcesso(mapped.processo);
    setError('');
    setShowMpPicker(false);
    setMpPickerEditIndex(null);
    if (mode === 'create') {
      setTemplateSourceId('');
    }
  }, [isOpen, product?.id, mode]);

  const handleImportarDeProdutoExistente = () => {
    const src = allProducts.find((p) => p.id === templateSourceId);
    if (!src) {
      setError('Selecione um produto para importar as configurações.');
      return;
    }
    const mapped = mapProductToModalForm(src, { cloneForNew: true });
    setNomeProduto(mapped.nomeProduto);
    setCodigo(mapped.codigo);
    setStatus('rascunho');
    setFormulacao(mapped.formulacao);
    setProcesso(mapped.processo);
    setError('');
    setActiveTab('formulacao');
  };

  const loadMateriasPrimas = async () => {
    try {
      setLoadingMaterias(true);
      const listProducts = httpsCallable(functions, 'listProducts');
      const result = await listProducts({
        empresa_id: getEmpresaId(),
        limit: 1000
      });

      if (result.data?.success) {
        const list = result.data.data?.products || result.data.data?.items || [];
        setMateriasPrimas(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Erro ao carregar matérias-primas:', err);
    } finally {
      setLoadingMaterias(false);
    }
  };

  const handleSave = async () => {
    // Validações
    if (!nomeProduto.trim()) {
      setError('Nome do produto é obrigatório');
      return;
    }

    if (!codigo.trim()) {
      setError('Código do produto é obrigatório');
      return;
    }

    if (formulacao.length === 0) {
      setError('Adicione pelo menos um ingrediente à formulação');
      return;
    }

    if (processo.length < 3) {
      setError(
        `O processo deve incluir "${ETAPA_ANCORA_SEPARACAO}", "${ETAPA_ANCORA_EMBALAGEM}" (penúltima) e "${ETAPA_ANCORA_ENCAIXOTAMENTO}" (última), com etapas intermediárias entre separação e embalagem.`
      );
      return;
    }
    const nomePrimeira = String(processo[0]?.nome || '').trim();
    const nomePenultima = String(processo[processo.length - 2]?.nome || '').trim();
    const nomeUltima = String(processo[processo.length - 1]?.nome || '').trim();
    if (
      nomePrimeira !== ETAPA_ANCORA_SEPARACAO ||
      nomePenultima !== ETAPA_ANCORA_EMBALAGEM ||
      nomeUltima !== ETAPA_ANCORA_ENCAIXOTAMENTO
    ) {
      setError(
        `A primeira etapa deve ser "${ETAPA_ANCORA_SEPARACAO}", a penúltima "${ETAPA_ANCORA_EMBALAGEM}" e a última "${ETAPA_ANCORA_ENCAIXOTAMENTO}".`
      );
      return;
    }

    for (const et of processo) {
      if (!isEtapaEmbalagemOuEncaixotamento(et.nome)) continue;
      const emb = normalizeEmbalagensPermitidas(et.embalagens_permitidas);
      if (emb.length === 0) {
        setError(
          `Etapa "${et.nome}": selecione ao menos uma embalagem (insumo) que pode ser utilizada.`
        );
        return;
      }
    }

    const formulacaoParaSalvar = formulacao.map((row) => ({
      ...row,
      quantidade_minima: parseDecimalComma(row.quantidade_minima),
      quantidade_maxima: parseDecimalComma(row.quantidade_maxima)
    }));

    for (let i = 0; i < formulacaoParaSalvar.length; i++) {
      const row = formulacaoParaSalvar[i];
      const nomeIng = row.materia_prima_nome || `Ingrediente ${i + 1}`;
      if (!Number.isFinite(row.quantidade_minima) || !Number.isFinite(row.quantidade_maxima)) {
        setError(`Informe quantidades mínima e máxima válidas para "${nomeIng}" (use vírgula como decimal, ex.: 10,5).`);
        return;
      }
      if (row.quantidade_minima < 0 || row.quantidade_maxima < 0) {
        setError('As quantidades não podem ser negativas.');
        return;
      }
      if (row.quantidade_maxima < row.quantidade_minima) {
        setError(`A quantidade máxima deve ser maior ou igual à mínima em "${nomeIng}".`);
        return;
      }
    }

    for (let ei = 0; ei < processo.length; ei++) {
      const et = processo[ei];
      for (const p of et.parametros || []) {
        if (p.tipo !== 'range') continue;
        const nMin = parseDecimalComma(p.min);
        const nMax = parseDecimalComma(p.max);
        if (!Number.isFinite(nMin) || !Number.isFinite(nMax)) {
          setError(
            `Parâmetro "${p.nome || '(sem nome)'}" na etapa "${et.nome || et.ordem || ei + 1}": informe mínimo e máximo válidos (vírgula como decimal; 0 é permitido).`
          );
          return;
        }
      }
      if (et.ciclos_ativo) {
        const ciclos = et.ciclos || [];
        if (ciclos.length === 0) {
          setError(
            `Etapa "${et.nome || et.ordem || ei + 1}": a opção de ciclos está ativa — adicione ao menos um ciclo ou desmarque.`
          );
          return;
        }
        for (let ci = 0; ci < ciclos.length; ci++) {
          const ciclo = ciclos[ci];
          const tempo = ciclo.tempo_estimado != null ? parseInt(String(ciclo.tempo_estimado), 10) : null;
          if (!Number.isFinite(tempo) || tempo <= 0) {
            setError(
              `Etapa "${et.nome || et.ordem || ei + 1}", ciclo ${ci + 1}: informe o tempo de processamento (${labelTempoUnidade(ciclo.tempo_unidade)}).`
            );
            return;
          }
          for (const p of ciclo.parametros || []) {
            if (p.tipo !== 'range') continue;
            const nMin = parseDecimalComma(p.min);
            const nMax = parseDecimalComma(p.max);
            if (!Number.isFinite(nMin) || !Number.isFinite(nMax)) {
              setError(
                `Ciclo ${ci + 1} da etapa "${et.nome || et.ordem || ei + 1}", parâmetro "${p.nome || '(sem nome)'}": informe mínimo e máximo válidos.`
              );
              return;
            }
          }
          for (let fi = 0; fi < (ciclo.fotos_ciclo || []).length; fi++) {
            if (!String(ciclo.fotos_ciclo[fi].nome || '').trim()) {
              setError(
                `Etapa "${et.nome || et.ordem || ei + 1}", ciclo ${ci + 1}: informe o nome da foto parametrizada #${fi + 1}.`
              );
              return;
            }
          }
        }
      }
    }

    for (let ei = 0; ei < processo.length; ei++) {
      const et = processo[ei];
      if (!et.fotos_etapa_ativo) continue;
      const fs = et.fotos_etapa || [];
      if (fs.length === 0) {
        setError(
          `Etapa "${et.nome || et.ordem || ei + 1}": a opção de fotos está ativa — adicione ao menos uma foto parametrizada ou desmarque "Adicionar fotos".`
        );
        return;
      }
      for (let j = 0; j < fs.length; j++) {
        if (!String(fs[j].nome || '').trim()) {
          setError(
            `Etapa "${et.nome || et.ordem || ei + 1}": informe o nome da foto parametrizada #${j + 1}.`
          );
          return;
        }
      }
    }

    try {
      setLoading(true);
      setError('');

      const functionName = mode === 'create' ? 'createProductionProduct' : 'updateProductionProduct';
      const saveFunction = httpsCallable(functions, functionName);

      const processoParaSalvar = normalizeProcessoParaApi(processo);

      const data = {
        nome_produto: nomeProduto.trim(),
        codigo: codigo.trim(),
        formulacao: formulacaoParaSalvar,
        processo_fabricacao: processoParaSalvar,
        status,
        empresa_id: getEmpresaId()
      };

      if (mode === 'edit') {
        data.product_id = product.id;
      }

      const result = await saveFunction(data);

      if (result.data.success) {
        onSave();
      } else {
        setError(result.data.message || 'Erro ao salvar produto');
      }
    } catch (err) {
      console.error('Erro ao salvar produto:', err);
      const msg =
        err?.message ||
        err?.details ||
        (typeof err === 'string' ? err : '') ||
        'Erro ao conectar com o servidor';
      setError(msg.includes('código') || msg.includes('Código') || msg.includes('already-exists')
        ? msg.replace(/^.*?:\s*/, '') || msg
        : msg.includes('Firebase')
          ? 'Erro ao conectar com o servidor'
          : msg);
    } finally {
      setLoading(false);
    }
  };

  // Funções da Formulação
  const openPickerNovoIngrediente = () => {
    setMpPickerEditIndex(null);
    setShowMpPicker(true);
  };

  const openPickerTrocarMp = (index) => {
    setMpPickerEditIndex(index);
    setShowMpPicker(true);
  };

  const handleMpPickerConfirm = (mp) => {
    const nome = getMpDisplayName(mp);
    const unidadePadrao = mp.unidade_medida && ['kg', 'g', 'L', 'mL', 'unidades', '%'].includes(mp.unidade_medida)
      ? mp.unidade_medida
      : 'kg';

    if (mpPickerEditIndex === null) {
      setFormulacao([
        {
          materia_prima_id: mp.id,
          materia_prima_nome: nome,
          quantidade_minima: '',
          quantidade_maxima: '',
          unidade: unidadePadrao,
          observacoes: ''
        },
        ...formulacao
      ]);
    } else {
      const newFormulacao = [...formulacao];
      newFormulacao[mpPickerEditIndex] = {
        ...newFormulacao[mpPickerEditIndex],
        materia_prima_id: mp.id,
        materia_prima_nome: nome
      };
      setFormulacao(newFormulacao);
    }
  };

  const mpPickerExcludeIds = useMemo(() => {
    return formulacao
      .map((f, i) => (mpPickerEditIndex !== null && i === mpPickerEditIndex ? null : f.materia_prima_id))
      .filter(Boolean);
  }, [formulacao, mpPickerEditIndex]);

  const updateIngrediente = (index, field, value) => {
    const newFormulacao = [...formulacao];
    newFormulacao[index][field] = value;
    
    // Se mudou a matéria-prima, atualizar o nome
    if (field === 'materia_prima_id') {
      const mp = materiasPrimas.find(m => m.id === value);
      if (mp) {
        newFormulacao[index].materia_prima_nome = mp.nome;
      }
    }
    
    setFormulacao(newFormulacao);
  };

  const removeIngrediente = (index) => {
    setFormulacao(formulacao.filter((_, i) => i !== index));
  };

  // Funções do Processo
  const addEtapa = () => {
    if (processo.length < 3) return;
    const key =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `etapa-${Date.now()}-${Math.random()}`;
    const insertAt = processo.length - 2;
    const novaEtapa = {
      _localKey: key,
      ordem: insertAt + 1,
      nome: '',
      descricao: '',
      tipo: 'processo',
      tempo_estimado: null,
      tempo_unidade: 'min',
      parametros: [],
      validacoes: [],
      fotos_etapa_ativo: false,
      fotos_etapa: [],
      peso_final_ativo: false,
      ciclos_ativo: false,
      ciclos: [],
      tipo_equipamento_id: '',
      tipo_equipamento_nome: '',
      cardinalidade: '1-1',
    };
    const next = [...processo.slice(0, insertAt), novaEtapa, ...processo.slice(insertAt)];
    next.forEach((etapa, i) => {
      etapa.ordem = i + 1;
    });
    setProcesso(next);
  };

  const reorderEtapas = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= processo.length ||
      toIndex >= processo.length
    ) {
      return;
    }
    const last = processo.length - 1;
    const penultimate = processo.length - 2;
    if (
      fromIndex === 0 ||
      fromIndex === last ||
      fromIndex === penultimate ||
      toIndex === 0 ||
      toIndex === last ||
      toIndex === penultimate
    ) {
      return;
    }
    const next = [...processo];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    next.forEach((etapa, i) => {
      etapa.ordem = i + 1;
    });
    setProcesso(next);
  };

  const updateEtapa = (index, field, value) => {
    const newProcesso = [...processo];
    const row = { ...newProcesso[index], [field]: value };
    if (field === 'tipo_equipamento_id') {
      const t = tiposEquipamento.find((x) => x.id === value);
      row.tipo_equipamento_nome = t?.nome || '';
    }
    newProcesso[index] = row;
    setProcesso(newProcesso);
  };

  const removeEtapa = (index) => {
    if (processo.length <= 3) return;
    if (index === 0 || index === processo.length - 1 || index === processo.length - 2) return;
    const newProcesso = processo.filter((_, i) => i !== index);
    // Renumerar as etapas
    newProcesso.forEach((etapa, i) => {
      etapa.ordem = i + 1;
    });
    setProcesso(newProcesso);
  };

  const addValidacao = (etapaIndex) => {
    const newProcesso = [...processo];
    if (!newProcesso[etapaIndex].validacoes) {
      newProcesso[etapaIndex].validacoes = [];
    }
    newProcesso[etapaIndex].validacoes.push({
      nome: '',
      valor_minimo: '',
      valor_maximo: '',
      tipo: ''
    });
    setProcesso(newProcesso);
  };

  const updateValidacao = (etapaIndex, validacaoIndex, field, value) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].validacoes[validacaoIndex][field] = value;
    setProcesso(newProcesso);
  };

  const removeValidacao = (etapaIndex, validacaoIndex) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].validacoes = newProcesso[etapaIndex].validacoes.filter((_, i) => i !== validacaoIndex);
    setProcesso(newProcesso);
  };

  // ✅ NOVO: Funções para Parâmetros
  const addParametro = (etapaIndex) => {
    const newProcesso = [...processo];
    if (!newProcesso[etapaIndex].parametros) {
      newProcesso[etapaIndex].parametros = [];
    }
    newProcesso[etapaIndex].parametros.push({
      nome: '',
      tipo: 'range',
      min: '',
      max: '',
      unidade: '',
      obrigatorio: true,
      critico: false
    });
    setProcesso(newProcesso);
  };

  const updateParametro = (etapaIndex, parametroIndex, field, value) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].parametros[parametroIndex][field] = value;
    setProcesso(newProcesso);
  };

  const removeParametro = (etapaIndex, parametroIndex) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].parametros = newProcesso[etapaIndex].parametros.filter((_, i) => i !== parametroIndex);
    setProcesso(newProcesso);
  };

  const setFotosEtapaAtivo = (etapaIndex, ativo) => {
    const newProcesso = [...processo];
    const et = { ...newProcesso[etapaIndex], fotos_etapa_ativo: ativo };
    if (ativo && (!et.fotos_etapa || et.fotos_etapa.length === 0)) {
      et.fotos_etapa = [
        {
          _localKeyFoto:
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `fk-${Date.now()}`,
          id:
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `id-${Date.now()}`,
          nome: '',
          instrucoes: ''
        }
      ];
    }
    if (!ativo) {
      et.fotos_etapa = [];
    }
    newProcesso[etapaIndex] = et;
    setProcesso(newProcesso);
  };

  const setPesoFinalAtivo = (etapaIndex, ativo) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex] = {
      ...newProcesso[etapaIndex],
      peso_final_ativo: !!ativo
    };
    setProcesso(newProcesso);
  };

  const toggleEmbalagemPermitida = (etapaIndex, embalagem) => {
    const newProcesso = [...processo];
    const et = { ...newProcesso[etapaIndex] };
    const atuais = normalizeEmbalagensPermitidas(et.embalagens_permitidas);
    const id = String(embalagem?.id || '').trim();
    if (!id) return;
    const exists = atuais.some((e) => e.id === id);
    et.embalagens_permitidas = exists
      ? atuais.filter((e) => e.id !== id)
      : [
          ...atuais,
          {
            id,
            nome: getMpDisplayName(embalagem),
            codigo: getMpCodigo(embalagem) || ''
          }
        ];
    newProcesso[etapaIndex] = et;
    setProcesso(newProcesso);
  };

  const setCiclosAtivo = (etapaIndex, ativo) => {
    const newProcesso = [...processo];
    const et = { ...newProcesso[etapaIndex], ciclos_ativo: !!ativo };
    if (ativo) {
      et.tempo_estimado = null;
      if (!et.ciclos || et.ciclos.length === 0) {
        et.ciclos = [
          {
            _localKeyCiclo: newModalLocalKey('ciclo'),
            id: newModalLocalKey('ciclo-id'),
            ordem: 1,
            tempo_estimado: '',
            tempo_unidade: 'min',
            parametros: [],
            fotos_ciclo: []
          }
        ];
      }
    } else {
      et.ciclos = [];
    }
    newProcesso[etapaIndex] = et;
    setProcesso(newProcesso);
  };

  const addCiclo = (etapaIndex) => {
    const newProcesso = [...processo];
    if (!newProcesso[etapaIndex].ciclos) newProcesso[etapaIndex].ciclos = [];
    newProcesso[etapaIndex].ciclos.push({
      _localKeyCiclo: newModalLocalKey('ciclo'),
      id: newModalLocalKey('ciclo-id'),
      ordem: newProcesso[etapaIndex].ciclos.length + 1,
      tempo_estimado: '',
      tempo_unidade: 'min',
      parametros: [],
      fotos_ciclo: []
    });
    newProcesso[etapaIndex].ciclos_ativo = true;
    newProcesso[etapaIndex].tempo_estimado = null;
    setProcesso(newProcesso);
  };

  const updateCiclo = (etapaIndex, cicloIndex, field, value) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].ciclos[cicloIndex][field] = value;
    setProcesso(newProcesso);
  };

  const removeCiclo = (etapaIndex, cicloIndex) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].ciclos = newProcesso[etapaIndex].ciclos.filter((_, i) => i !== cicloIndex);
    newProcesso[etapaIndex].ciclos.forEach((c, i) => {
      c.ordem = i + 1;
    });
    if (newProcesso[etapaIndex].ciclos.length === 0) {
      newProcesso[etapaIndex].ciclos_ativo = false;
    }
    setProcesso(newProcesso);
  };

  const addParametroCiclo = (etapaIndex, cicloIndex) => {
    const newProcesso = [...processo];
    if (!newProcesso[etapaIndex].ciclos[cicloIndex].parametros) {
      newProcesso[etapaIndex].ciclos[cicloIndex].parametros = [];
    }
    newProcesso[etapaIndex].ciclos[cicloIndex].parametros.push({
      nome: '',
      tipo: 'range',
      min: '',
      max: '',
      unidade: '',
      obrigatorio: true,
      critico: false
    });
    setProcesso(newProcesso);
  };

  const updateParametroCiclo = (etapaIndex, cicloIndex, parametroIndex, field, value) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].ciclos[cicloIndex].parametros[parametroIndex][field] = value;
    setProcesso(newProcesso);
  };

  const removeParametroCiclo = (etapaIndex, cicloIndex, parametroIndex) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].ciclos[cicloIndex].parametros = newProcesso[etapaIndex].ciclos[
      cicloIndex
    ].parametros.filter((_, i) => i !== parametroIndex);
    setProcesso(newProcesso);
  };

  const addFotoCiclo = (etapaIndex, cicloIndex) => {
    const newProcesso = [...processo];
    const ciclo = newProcesso[etapaIndex].ciclos[cicloIndex];
    if (!ciclo.fotos_ciclo) ciclo.fotos_ciclo = [];
    ciclo.fotos_ciclo.push({
      _localKeyFoto: newModalLocalKey('foto'),
      id: newModalLocalKey('foto-id'),
      nome: '',
      instrucoes: ''
    });
    setProcesso(newProcesso);
  };

  const updateFotoCiclo = (etapaIndex, cicloIndex, fotoIndex, field, value) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].ciclos[cicloIndex].fotos_ciclo[fotoIndex][field] = value;
    setProcesso(newProcesso);
  };

  const removeFotoCiclo = (etapaIndex, cicloIndex, fotoIndex) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].ciclos[cicloIndex].fotos_ciclo = newProcesso[etapaIndex].ciclos[
      cicloIndex
    ].fotos_ciclo.filter((_, i) => i !== fotoIndex);
    setProcesso(newProcesso);
  };

  const addFotoEtapa = (etapaIndex) => {
    const newProcesso = [...processo];
    if (!newProcesso[etapaIndex].fotos_etapa) newProcesso[etapaIndex].fotos_etapa = [];
    newProcesso[etapaIndex].fotos_etapa.push({
      _localKeyFoto:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `fk-${Date.now()}`,
      id:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `id-${Date.now()}`,
      nome: '',
      instrucoes: ''
    });
    newProcesso[etapaIndex].fotos_etapa_ativo = true;
    setProcesso(newProcesso);
  };

  const updateFotoEtapa = (etapaIndex, fotoIndex, field, value) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].fotos_etapa[fotoIndex][field] = value;
    setProcesso(newProcesso);
  };

  const removeFotoEtapa = (etapaIndex, fotoIndex) => {
    const newProcesso = [...processo];
    newProcesso[etapaIndex].fotos_etapa = newProcesso[etapaIndex].fotos_etapa.filter((_, i) => i !== fotoIndex);
    if (newProcesso[etapaIndex].fotos_etapa.length === 0) {
      newProcesso[etapaIndex].fotos_etapa_ativo = false;
    }
    setProcesso(newProcesso);
  };

  const isViewMode = mode === 'view';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-5/6 lg:w-4/5 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? 'Novo Produto' : 
             mode === 'edit' ? 'Editar Produto' : 
             'Visualizar Produto'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Informações Básicas */}
        <div className="mb-6 pb-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Produto *
              </label>
              <input
                type="text"
                value={nomeProduto}
                onChange={(e) => setNomeProduto(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Ex: Ração Premium para Cães Adultos"
                disabled={isViewMode || loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código do Produto *
              </label>
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono"
                placeholder="Ex: PF-001"
                disabled={isViewMode || loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                disabled={isViewMode || loading}
              >
                <option value="rascunho">Rascunho</option>
                <option value="ativo">Ativo</option>
                <option value="arquivado">Arquivado</option>
              </select>
            </div>

            {mode === 'create' && allProducts.length > 0 && (
              <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Carregar configurações de um produto existente (opcional)
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Copia <strong>formulação</strong> (ingredientes e quantidades) e <strong>processo de fabricação</strong>
                  (etapas, parâmetros, validações e fotos parametrizadas). Edite o nome e o restante antes de salvar.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <select
                    value={templateSourceId}
                    onChange={(e) => setTemplateSourceId(e.target.value)}
                    className="min-w-0 flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    disabled={isViewMode || loading}
                  >
                    <option value="">Não importar — começar em branco</option>
                    {allProducts.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.codigo ? `${p.codigo} — ` : ''}
                        {p.nome_produto}
                        {p.versao ? ` (${p.versao})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleImportarDeProdutoExistente}
                    disabled={!templateSourceId || isViewMode || loading}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Copy className="h-4 w-4" />
                    Importar
                  </button>
                </div>
              </div>
            )}
          </div>

          {product?.versao && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-700">
                <strong>Versão atual:</strong> {product.versao}
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('formulacao')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'formulacao'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Beaker className="h-5 w-5 mr-2" />
                Formulação ({formulacao.length})
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('processo')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'processo'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <List className="h-5 w-5 mr-2" />
                Processo de Fabricação ({processo.length})
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'formulacao' && (
          <FormulacaoTab
            formulacao={formulacao}
            materiasPrimas={materiasPrimas}
            loadingMaterias={loadingMaterias}
            onRequestAddIngredient={openPickerNovoIngrediente}
            onRequestChangeMp={openPickerTrocarMp}
            onUpdate={updateIngrediente}
            onRemove={removeIngrediente}
            isViewMode={isViewMode}
            loading={loading}
          />
        )}

        <MateriaPrimaPickerModal
          isOpen={showMpPicker}
          onClose={() => {
            setShowMpPicker(false);
            setMpPickerEditIndex(null);
          }}
          materiasPrimas={materiasPrimas}
          loadingMaterias={loadingMaterias}
          onConfirm={handleMpPickerConfirm}
          excludeIds={mpPickerExcludeIds}
        />

        {activeTab === 'processo' && (
          <ProcessoTab
            processo={processo}
            tiposEquipamento={tiposEquipamento}
            onAddEtapa={addEtapa}
            onUpdateEtapa={updateEtapa}
            onRemoveEtapa={removeEtapa}
            onReorderEtapas={reorderEtapas}
            onAddValidacao={addValidacao}
            onUpdateValidacao={updateValidacao}
            onRemoveValidacao={removeValidacao}
            onAddParametro={addParametro}
            onUpdateParametro={updateParametro}
            onRemoveParametro={removeParametro}
            onSetFotosEtapaAtivo={setFotosEtapaAtivo}
            onSetPesoFinalAtivo={setPesoFinalAtivo}
            onToggleEmbalagemPermitida={toggleEmbalagemPermitida}
            embalagensInsumos={materiasPrimas.filter(isInsumoEmbalagem)}
            onSetCiclosAtivo={setCiclosAtivo}
            onAddCiclo={addCiclo}
            onUpdateCiclo={updateCiclo}
            onRemoveCiclo={removeCiclo}
            onAddParametroCiclo={addParametroCiclo}
            onUpdateParametroCiclo={updateParametroCiclo}
            onRemoveParametroCiclo={removeParametroCiclo}
            onAddFotoCiclo={addFotoCiclo}
            onUpdateFotoCiclo={updateFotoCiclo}
            onRemoveFotoCiclo={removeFotoCiclo}
            onAddFotoEtapa={addFotoEtapa}
            onUpdateFotoEtapa={updateFotoEtapa}
            onRemoveFotoEtapa={removeFotoEtapa}
            isViewMode={isViewMode}
            loading={loading}
          />
        )}

        {/* Footer */}
        {!isViewMode && (
          <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Salvar Produto
                </>
              )}
            </button>
          </div>
        )}

        {isViewMode && (
          <div className="flex justify-end pt-6 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== FORMULAÇÃO TAB ====================

const FormulacaoTab = ({ 
  formulacao, 
  materiasPrimas, 
  loadingMaterias,
  onRequestAddIngredient, 
  onRequestChangeMp,
  onUpdate, 
  onRemove, 
  isViewMode,
  loading
}) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-900">Ingredientes da Formulação</h4>
        {!isViewMode && (
          <button
            type="button"
            onClick={onRequestAddIngredient}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md flex items-center gap-2 text-sm"
            disabled={loading || loadingMaterias}
          >
            <Plus className="h-4 w-4" />
            Adicionar Item
          </button>
        )}
      </div>

      {formulacao.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Beaker className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">Nenhum ingrediente adicionado</p>
          {!isViewMode && (
            <button
              type="button"
              onClick={onRequestAddIngredient}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Adicionar primeiro ingrediente
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {(formulacao || []).map((item, index) => {
            const mpSelecionada = (materiasPrimas || []).find((m) => m.id === item.materia_prima_id);
            const codMp = getMpCodigo(mpSelecionada || {});
            return (
            <div
              key={`${item.materia_prima_id || 'sem-id'}-${index}`}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Ingrediente */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ingrediente *
                  </label>
                  {loadingMaterias ? (
                    <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-500">
                      Carregando...
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2 bg-white text-sm min-h-[42px] flex items-center">
                        {item.materia_prima_id ? (
                          <div>
                            <span className="font-medium text-gray-900">{item.materia_prima_nome || getMpDisplayName(mpSelecionada) || '—'}</span>
                            {codMp ? (
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Cód.: {codMp}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-gray-400">Nenhuma selecionada — use &quot;Escolher&quot;</span>
                        )}
                      </div>
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={() => onRequestChangeMp(index)}
                          className="shrink-0 px-3 py-2 border border-blue-600 text-blue-700 bg-blue-50 rounded-md text-sm font-medium hover:bg-blue-100"
                          disabled={loading}
                        >
                          {item.materia_prima_id ? 'Trocar' : 'Escolher'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Quantidade Mínima */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qtd. Mínima *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={item.quantidade_minima ?? ''}
                    onChange={(e) => onUpdate(index, 'quantidade_minima', e.target.value)}
                    placeholder="ex: 10,5"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    disabled={isViewMode || loading}
                  />
                </div>

                {/* Quantidade Máxima */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qtd. Máxima *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={item.quantidade_maxima ?? ''}
                    onChange={(e) => onUpdate(index, 'quantidade_maxima', e.target.value)}
                    placeholder="ex: 12,0"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    disabled={isViewMode || loading}
                  />
                </div>

                {/* Unidade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidade *
                  </label>
                  <select
                    value={item.unidade}
                    onChange={(e) => onUpdate(index, 'unidade', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    disabled={isViewMode || loading}
                  >
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="L">L</option>
                    <option value="mL">mL</option>
                    <option value="unidades">unidades</option>
                    <option value="%">%</option>
                  </select>
                </div>
              </div>

              {/* Observações */}
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <input
                  type="text"
                  value={item.observacoes || ''}
                  onChange={(e) => onUpdate(index, 'observacoes', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Observações sobre este ingrediente..."
                  disabled={isViewMode || loading}
                />
              </div>

              {/* Botão Remover */}
              {!isViewMode && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => onRemove(index)}
                    className="text-red-600 hover:text-red-900 text-sm flex items-center gap-1"
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                    Remover
                  </button>
                </div>
              )}
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== PROCESSO TAB ====================

const ProcessoTab = ({
  processo,
  tiposEquipamento = [],
  onAddEtapa,
  onUpdateEtapa,
  onRemoveEtapa,
  onReorderEtapas,
  onAddValidacao,
  onUpdateValidacao,
  onRemoveValidacao,
  onAddParametro,
  onUpdateParametro,
  onRemoveParametro,
  onSetFotosEtapaAtivo,
  onSetPesoFinalAtivo,
  onToggleEmbalagemPermitida,
  embalagensInsumos = [],
  onSetCiclosAtivo,
  onAddCiclo,
  onUpdateCiclo,
  onRemoveCiclo,
  onAddParametroCiclo,
  onUpdateParametroCiclo,
  onRemoveParametroCiclo,
  onAddFotoCiclo,
  onUpdateFotoCiclo,
  onRemoveFotoCiclo,
  onAddFotoEtapa,
  onUpdateFotoEtapa,
  onRemoveFotoEtapa,
  isViewMode,
  loading
}) => {
  const [dragOverIndex, setDragOverIndex] = useState(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Etapas do Processo de Fabricação</h4>
          <p className="text-xs text-gray-600 mt-1 max-w-xl">
            A primeira etapa é sempre <strong>Separação e Pesagem</strong>, a penúltima <strong>Embalagem</strong> e a
            última <strong>Encaixotamento</strong>. Novas etapas são inseridas entre a separação e a embalagem.
          </p>
          {!isViewMode && processo.length > 3 && (
            <p className="text-xs text-gray-500 mt-1">
              Arraste pelo ícone <span className="inline-flex align-middle text-gray-400">⋮⋮</span> para reordenar só as
              etapas intermediárias.
            </p>
          )}
        </div>
        {!isViewMode && (
          <button
            onClick={onAddEtapa}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md flex items-center gap-2 text-sm"
            disabled={loading}
          >
            <Plus className="h-4 w-4" />
            Adicionar Etapa
          </button>
        )}
      </div>

      {processo.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <List className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">Nenhuma etapa adicionada</p>
          {!isViewMode && (
            <button
              onClick={onAddEtapa}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Adicionar primeira etapa
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {processo.map((etapa, etapaIndex) => (
            <div
              key={etapa._localKey || `etapa-fallback-${etapaIndex}`}
              className={`border-2 rounded-lg p-4 bg-white transition-colors ${
                dragOverIndex === etapaIndex ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
              }`}
              onDragOver={(e) => {
                if (isViewMode) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragEnter={() => {
                if (!isViewMode) setDragOverIndex(etapaIndex);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setDragOverIndex((prev) => (prev === etapaIndex ? null : prev));
                }
              }}
              onDrop={(e) => {
                if (isViewMode) return;
                e.preventDefault();
                const fromStr = e.dataTransfer.getData('application/x-etapa-index');
                const from = parseInt(fromStr, 10);
                setDragOverIndex(null);
                if (!Number.isNaN(from)) {
                  onReorderEtapas(from, etapaIndex);
                }
              }}
            >
              <div className="flex items-start justify-between mb-4 gap-2">
                <div className="flex items-center min-w-0 flex-1">
                  {!isViewMode && etapaIndex > 0 && etapaIndex < processo.length - 1 && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Arrastar etapa ${etapa.ordem} para reordenar`}
                      title="Arrastar para reordenar"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/x-etapa-index', String(etapaIndex));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => setDragOverIndex(null)}
                      className="mr-2 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-grab active:cursor-grabbing shrink-0"
                    >
                      <GripVertical className="h-5 w-5" aria-hidden />
                    </div>
                  )}
                  <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 shrink-0">
                    {etapa.ordem}
                  </div>
                  <h5 className="text-lg font-medium text-gray-900 truncate">Etapa {etapa.ordem}</h5>
                </div>
                {!isViewMode && etapaIndex > 0 && etapaIndex < processo.length - 2 && (
                  <button
                    type="button"
                    onClick={() => onRemoveEtapa(etapaIndex)}
                    className="text-red-600 hover:text-red-900 text-sm shrink-0"
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Etapa *
                  </label>
                  <input
                    type="text"
                    value={etapa.nome}
                    onChange={(e) => onUpdateEtapa(etapaIndex, 'nome', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Ex: Mistura, Cozimento, Resfriamento..."
                    disabled={
                      isViewMode ||
                      loading ||
                      etapaIndex === 0 ||
                      etapaIndex === processo.length - 1 ||
                      etapaIndex === processo.length - 2
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <input
                    type="text"
                    value={etapa.descricao}
                    onChange={(e) => onUpdateEtapa(etapaIndex, 'descricao', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Descrição detalhada da etapa..."
                    disabled={isViewMode || loading}
                  />
                </div>
              </div>

              {isEtapaEmbalagemOuEncaixotamento(etapa.nome) && (
                <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-4 w-4 text-amber-800" />
                    <h6 className="text-sm font-medium text-amber-900">
                      {etapa.nome === ETAPA_ANCORA_ENCAIXOTAMENTO
                        ? 'Embalagens de encaixotamento permitidas'
                        : 'Embalagens permitidas'}
                    </h6>
                  </div>
                  <p className="text-xs text-amber-800 mb-3">
                    Selecione uma ou mais embalagens do cadastro de insumos (tipo Embalagem) que podem ser
                    utilizadas nesta etapa.
                  </p>
                  {embalagensInsumos.length === 0 ? (
                    <p className="text-sm text-amber-700 italic">
                      Nenhuma embalagem cadastrada em Insumos → Embalagens.
                    </p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1.5 rounded border border-amber-100 bg-white p-2">
                      {embalagensInsumos
                        .slice()
                        .sort((a, b) =>
                          getMpDisplayName(a).localeCompare(getMpDisplayName(b), 'pt-BR')
                        )
                        .map((emb) => {
                          const checked = (etapa.embalagens_permitidas || []).some(
                            (e) => e.id === emb.id
                          );
                          return (
                            <label
                              key={emb.id}
                              className="flex items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-amber-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 rounded"
                                checked={checked}
                                disabled={isViewMode || loading}
                                onChange={() => onToggleEmbalagemPermitida(etapaIndex, emb)}
                              />
                              <span className="min-w-0">
                                <span className="font-medium text-gray-900">{getMpDisplayName(emb)}</span>
                                {getMpCodigo(emb) ? (
                                  <span className="text-xs text-gray-500 ml-2">{getMpCodigo(emb)}</span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  )}
                  {(etapa.embalagens_permitidas || []).length > 0 && (
                    <p className="text-xs text-amber-800 mt-2">
                      {(etapa.embalagens_permitidas || []).length} selecionada(s)
                    </p>
                  )}
                </div>
              )}

              {/* ✅ Tipo e Tempo / Ciclos */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Etapa *
                  </label>
                  <select
                    value={etapa.tipo || 'processo'}
                    onChange={(e) => onUpdateEtapa(etapaIndex, 'tipo', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    disabled={isViewMode || loading}
                  >
                    <option value="processo">🔄 Processo (com tempo)</option>
                    <option value="medicao">📊 Medição (validação manual)</option>
                  </select>
                </div>

                {etapa.tipo !== 'medicao' && !etapa.ciclos_ativo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tempo estimado
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={etapa.tempo_estimado || ''}
                        onChange={(e) =>
                          onUpdateEtapa(
                            etapaIndex,
                            'tempo_estimado',
                            e.target.value ? parseInt(e.target.value, 10) : null
                          )
                        }
                        className="min-w-0 flex-1 border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Ex: 35"
                        min="1"
                        disabled={isViewMode || loading}
                      />
                      <select
                        value={normalizeTempoUnidade(etapa.tempo_unidade)}
                        onChange={(e) => onUpdateEtapa(etapaIndex, 'tempo_unidade', e.target.value)}
                        className="w-32 border border-gray-300 rounded-md px-2 py-2 text-sm"
                        disabled={isViewMode || loading}
                      >
                        {TEMPO_UNIDADES.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      ⏱️ Timer automático no Tempo Real
                    </p>
                  </div>
                )}

                {etapa.tipo !== 'medicao' && (
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
                      <input
                        type="checkbox"
                        checked={!!etapa.ciclos_ativo}
                        onChange={(e) => onSetCiclosAtivo(etapaIndex, e.target.checked)}
                        className="rounded"
                        disabled={isViewMode || loading}
                      />
                      Processar em ciclos
                    </label>
                  </div>
                )}
              </div>

              {etapa.ciclos_ativo && etapa.tipo !== 'medicao' && (
                <div className="mb-4 border border-indigo-200 bg-indigo-50 rounded-lg p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <h6 className="text-sm font-medium text-indigo-900">Ciclos de processamento</h6>
                      <p className="text-xs text-indigo-700 mt-0.5">
                        Cada ciclo tem seu tempo e parâmetros ao final. Os parâmetros da etapa (abaixo) são
                        coletados após concluir todos os ciclos.
                      </p>
                    </div>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => onAddCiclo(etapaIndex)}
                        className="text-indigo-700 hover:text-indigo-900 text-sm flex items-center gap-1"
                        disabled={loading}
                      >
                        <Plus className="h-3 w-3" />
                        Adicionar ciclo
                      </button>
                    )}
                  </div>
                  {(!etapa.ciclos || etapa.ciclos.length === 0) ? (
                    <p className="text-sm text-indigo-600 italic">Nenhum ciclo definido</p>
                  ) : (
                    <div className="space-y-4">
                      {etapa.ciclos.map((ciclo, cicloIndex) => (
                        <div
                          key={ciclo._localKeyCiclo || ciclo.id || cicloIndex}
                          className="bg-white border border-indigo-200 rounded-lg p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <span className="text-sm font-semibold text-indigo-900">
                              Ciclo {cicloIndex + 1}
                            </span>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={() => onRemoveCiclo(etapaIndex, cicloIndex)}
                                className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1"
                                disabled={loading}
                              >
                                <Trash2 className="h-3 w-3" />
                                Remover ciclo
                              </button>
                            )}
                          </div>
                          <div className="mb-3 max-w-md">
                            <label className="block text-xs text-gray-600 mb-1">
                              Tempo do ciclo *
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                min="1"
                                value={ciclo.tempo_estimado ?? ''}
                                onChange={(e) =>
                                  onUpdateCiclo(
                                    etapaIndex,
                                    cicloIndex,
                                    'tempo_estimado',
                                    e.target.value ? parseInt(e.target.value, 10) : ''
                                  )
                                }
                                className="min-w-0 flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                                placeholder="Ex: 15"
                                disabled={isViewMode || loading}
                              />
                              <select
                                value={normalizeTempoUnidade(ciclo.tempo_unidade)}
                                onChange={(e) =>
                                  onUpdateCiclo(etapaIndex, cicloIndex, 'tempo_unidade', e.target.value)
                                }
                                className="w-32 border border-gray-300 rounded px-2 py-1.5 text-sm"
                                disabled={isViewMode || loading}
                              >
                                {TEMPO_UNIDADES.map((u) => (
                                  <option key={u.value} value={u.value}>
                                    {u.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-700">
                              Parâmetros ao fim do ciclo
                            </span>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={() => onAddParametroCiclo(etapaIndex, cicloIndex)}
                                className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                                disabled={loading}
                              >
                                <Plus className="h-3 w-3" />
                                Parâmetro
                              </button>
                            )}
                          </div>
                          {(!ciclo.parametros || ciclo.parametros.length === 0) ? (
                            <p className="text-xs text-gray-500 italic">
                              Nenhum parâmetro neste ciclo (opcional)
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {ciclo.parametros.map((param, paramIndex) => (
                                <div
                                  key={paramIndex}
                                  className="bg-blue-50 border border-blue-200 p-2 rounded"
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-1">
                                    <div className="md:col-span-2">
                                      <input
                                        type="text"
                                        value={param.nome}
                                        onChange={(e) =>
                                          onUpdateParametroCiclo(
                                            etapaIndex,
                                            cicloIndex,
                                            paramIndex,
                                            'nome',
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                        placeholder="Nome"
                                        disabled={isViewMode || loading}
                                      />
                                    </div>
                                    <div>
                                      <select
                                        value={param.tipo}
                                        onChange={(e) =>
                                          onUpdateParametroCiclo(
                                            etapaIndex,
                                            cicloIndex,
                                            paramIndex,
                                            'tipo',
                                            e.target.value
                                          )
                                        }
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                        disabled={isViewMode || loading}
                                      >
                                        <option value="range">Faixa</option>
                                        <option value="texto">Texto</option>
                                        <option value="boolean">Sim/Não</option>
                                      </select>
                                    </div>
                                    {param.tipo === 'range' && (
                                      <>
                                        <div>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={param.min ?? ''}
                                            onChange={(e) =>
                                              onUpdateParametroCiclo(
                                                etapaIndex,
                                                cicloIndex,
                                                paramIndex,
                                                'min',
                                                e.target.value
                                              )
                                            }
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                            placeholder="Mín"
                                            disabled={isViewMode || loading}
                                          />
                                        </div>
                                        <div>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={param.max ?? ''}
                                            onChange={(e) =>
                                              onUpdateParametroCiclo(
                                                etapaIndex,
                                                cicloIndex,
                                                paramIndex,
                                                'max',
                                                e.target.value
                                              )
                                            }
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                            placeholder="Máx"
                                            disabled={isViewMode || loading}
                                          />
                                        </div>
                                        <div>
                                          <input
                                            type="text"
                                            value={param.unidade || ''}
                                            onChange={(e) =>
                                              onUpdateParametroCiclo(
                                                etapaIndex,
                                                cicloIndex,
                                                paramIndex,
                                                'unidade',
                                                e.target.value
                                              )
                                            }
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                            placeholder="Unidade"
                                            disabled={isViewMode || loading}
                                          />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-1 text-xs text-gray-600">
                                      <input
                                        type="checkbox"
                                        checked={param.obrigatorio !== false}
                                        onChange={(e) =>
                                          onUpdateParametroCiclo(
                                            etapaIndex,
                                            cicloIndex,
                                            paramIndex,
                                            'obrigatorio',
                                            e.target.checked
                                          )
                                        }
                                        className="rounded"
                                        disabled={isViewMode || loading}
                                      />
                                      Obrigatório
                                    </label>
                                    {!isViewMode && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          onRemoveParametroCiclo(etapaIndex, cicloIndex, paramIndex)
                                        }
                                        className="ml-auto text-red-600"
                                        disabled={loading}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-3 border-t border-indigo-100 pt-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs font-medium text-gray-700 flex items-center gap-1">
                                <Camera className="h-3 w-3" />
                                Fotos ao fim do ciclo (opcional)
                              </span>
                              {!isViewMode && (
                                <button
                                  type="button"
                                  onClick={() => onAddFotoCiclo(etapaIndex, cicloIndex)}
                                  className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                                  disabled={loading}
                                >
                                  <Plus className="h-3 w-3" />
                                  Foto
                                </button>
                              )}
                            </div>
                            {(!ciclo.fotos_ciclo || ciclo.fotos_ciclo.length === 0) ? (
                              <p className="text-xs text-gray-500 italic">
                                Nenhuma foto neste ciclo (opcional)
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {ciclo.fotos_ciclo.map((foto, fotoIndex) => (
                                  <div
                                    key={foto._localKeyFoto || foto.id || fotoIndex}
                                    className="bg-amber-50 border border-amber-200 rounded p-2 space-y-2"
                                  >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      <div>
                                        <label className="text-xs text-gray-600">Nome da foto *</label>
                                        <input
                                          type="text"
                                          value={foto.nome}
                                          onChange={(e) =>
                                            onUpdateFotoCiclo(
                                              etapaIndex,
                                              cicloIndex,
                                              fotoIndex,
                                              'nome',
                                              e.target.value
                                            )
                                          }
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                          placeholder="Ex: Painel do ciclo"
                                          disabled={isViewMode || loading}
                                        />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-600">Instruções</label>
                                        <textarea
                                          value={foto.instrucoes || ''}
                                          onChange={(e) =>
                                            onUpdateFotoCiclo(
                                              etapaIndex,
                                              cicloIndex,
                                              fotoIndex,
                                              'instrucoes',
                                              e.target.value
                                            )
                                          }
                                          rows={2}
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                          placeholder="O que registrar na imagem..."
                                          disabled={isViewMode || loading}
                                        />
                                      </div>
                                    </div>
                                    {!isViewMode && (
                                      <div className="flex justify-end">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            onRemoveFotoCiclo(etapaIndex, cicloIndex, fotoIndex)
                                          }
                                          className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1"
                                          disabled={loading}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                          Remover
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de equipamento (Tempo Real)
                </label>
                <select
                  value={etapa.tipo_equipamento_id || ''}
                  onChange={(e) => onUpdateEtapa(etapaIndex, 'tipo_equipamento_id', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isViewMode || loading}
                >
                  <option value="">Nenhum / não aplicável</option>
                  {tiposEquipamento.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome} — {t.nivel1_area}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Operador poderá informar qual equipamento foi usado (opcional) ao concluir a etapa.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cardinalidade de carrinhos (Fluxo por Setor)
                </label>
                <select
                  value={etapa.cardinalidade || '1-1'}
                  onChange={(e) => onUpdateEtapa(etapaIndex, 'cardinalidade', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isViewMode || loading}
                >
                  <option value="1-1">1 → 1 (entra 1 carrinho, sai 1 carrinho)</option>
                  <option value="1-n">1 → N (entra 1 carrinho, podem sair vários)</option>
                  <option value="n-1">N → 1 (agrupa vários carrinhos, sai 1)</option>
                  <option value="n-m">N → M (agrupa vários, podem sair vários)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Define como o operador processa os carrinhos nesta etapa: individual (1→…) ou agrupando vários
                  no equipamento (N→…).
                </p>
              </div>

              {/* ✅ Parâmetros da etapa (fim dos ciclos ou etapa simples) */}
              <div className="mt-4 border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h6 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <ThermometerSun className="h-4 w-4" />
                    Parâmetros de Qualidade
                    {etapa.ciclos_ativo ? (
                      <span className="text-xs font-normal text-gray-500">(ao fim dos ciclos)</span>
                    ) : null}
                  </h6>
                  {!isViewMode && (
                    <button
                      onClick={() => onAddParametro(etapaIndex)}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                      disabled={loading}
                    >
                      <Plus className="h-3 w-3" />
                      Adicionar Parâmetro
                    </button>
                  )}
                </div>

                {(!etapa.parametros || etapa.parametros.length === 0) ? (
                  <p className="text-sm text-gray-500 italic">
                    Nenhum parâmetro definido. Ex: Temperatura, pH, Umidade
                  </p>
                ) : (
                  <div className="space-y-3">
                    {etapa.parametros.map((param, paramIndex) => (
                      <div key={paramIndex} className="bg-blue-50 border border-blue-200 p-3 rounded">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2">
                          <div className="md:col-span-2">
                            <input
                              type="text"
                              value={param.nome}
                              onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'nome', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder="Nome (ex: Temperatura)"
                              disabled={isViewMode || loading}
                            />
                          </div>
                          <div>
                            <select
                              value={param.tipo}
                              onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'tipo', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              disabled={isViewMode || loading}
                            >
                              <option value="range">📊 Faixa (min-max)</option>
                              <option value="texto">📝 Texto</option>
                              <option value="boolean">✓ Sim/Não</option>
                            </select>
                          </div>
                          {param.tipo === 'range' && (
                            <>
                              <div>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={param.min ?? ''}
                                  onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'min', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="Mín (ex: 0,5)"
                                  disabled={isViewMode || loading}
                                />
                              </div>
                              <div>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={param.max ?? ''}
                                  onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'max', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="Máx (ex: 100)"
                                  disabled={isViewMode || loading}
                                />
                              </div>
                              <div>
                                <input
                                  type="text"
                                  value={param.unidade || ''}
                                  onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'unidade', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="Unidade"
                                  disabled={isViewMode || loading}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={param.obrigatorio !== false}
                              onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'obrigatorio', e.target.checked)}
                              className="rounded"
                              disabled={isViewMode || loading}
                            />
                            Obrigatório
                          </label>
                          <label className="flex items-center gap-1 text-xs text-red-600">
                            <input
                              type="checkbox"
                              checked={param.critico || false}
                              onChange={(e) => onUpdateParametro(etapaIndex, paramIndex, 'critico', e.target.checked)}
                              className="rounded text-red-600"
                              disabled={isViewMode || loading}
                            />
                            Crítico (necessita análise técnica)
                          </label>
                          {!isViewMode && (
                            <button
                              onClick={() => onRemoveParametro(etapaIndex, paramIndex)}
                              className="ml-auto text-red-600 hover:text-red-900"
                              disabled={loading}
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Validações */}
              <div className="mt-4 border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h6 className="text-sm font-medium text-gray-700">Parâmetros de Produção</h6>
                  {!isViewMode && (
                    <button
                      onClick={() => onAddValidacao(etapaIndex)}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                      disabled={loading}
                    >
                      <Plus className="h-3 w-3" />
                      Adicionar Validação
                    </button>
                  )}
                </div>

                {(!etapa.validacoes || etapa.validacoes.length === 0) ? (
                  <p className="text-sm text-gray-500 italic">Nenhuma validação definida</p>
                ) : (
                  <div className="space-y-3">
                    {etapa.validacoes.map((validacao, validacaoIndex) => (
                      <div
                        key={validacaoIndex}
                        className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-2 bg-gray-50 p-3 rounded border border-gray-200"
                      >
                        <div className="flex-1 min-w-[140px]">
                          <label className="text-xs text-gray-500">Nome</label>
                          <input
                            type="text"
                            value={validacao.nome}
                            onChange={(e) => onUpdateValidacao(etapaIndex, validacaoIndex, 'nome', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="Ex: pH, Umidade"
                            disabled={isViewMode || loading}
                          />
                        </div>
                        <div className="w-full sm:w-auto sm:min-w-[100px]">
                          <label className="text-xs text-gray-500">Mín (aceita 0, vírgula decimal)</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={validacao.valor_minimo ?? ''}
                            onChange={(e) =>
                              onUpdateValidacao(etapaIndex, validacaoIndex, 'valor_minimo', e.target.value)
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="Mín"
                            disabled={isViewMode || loading}
                          />
                        </div>
                        <div className="w-full sm:w-auto sm:min-w-[100px]">
                          <label className="text-xs text-gray-500">Máx</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={validacao.valor_maximo ?? ''}
                            onChange={(e) =>
                              onUpdateValidacao(etapaIndex, validacaoIndex, 'valor_maximo', e.target.value)
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="Máx"
                            disabled={isViewMode || loading}
                          />
                        </div>
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => onRemoveValidacao(etapaIndex, validacaoIndex)}
                            className="text-red-600 hover:text-red-900 sm:ml-auto p-1"
                            disabled={loading}
                            aria-label="Remover validação"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Peso final (opcional por etapa — captura na fabricação) */}
              <div className="mt-4 border-t pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <h6 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Peso final
                  </h6>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!etapa.peso_final_ativo}
                      onChange={(e) => onSetPesoFinalAtivo(etapaIndex, e.target.checked)}
                      className="rounded"
                      disabled={isViewMode || loading}
                    />
                    Capturar peso final
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  {etapa.peso_final_ativo
                    ? 'Na fabricação, o operador deverá informar o peso final ao concluir esta etapa.'
                    : 'Desativado — o peso final não será solicitado nesta etapa.'}
                </p>
              </div>

              {/* Fotos obrigatórias na etapa (execução em Tempo Real) */}
              <div className="mt-4 border-t pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h6 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Fotos na etapa
                  </h6>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!etapa.fotos_etapa_ativo}
                      onChange={(e) => onSetFotosEtapaAtivo(etapaIndex, e.target.checked)}
                      className="rounded"
                      disabled={isViewMode || loading}
                    />
                    Adicionar fotos
                  </label>
                </div>
                {etapa.fotos_etapa_ativo ? (
                  <>
                    <p className="text-xs text-gray-500 mb-3">
                      Defina cada registro fotográfico exigido ao finalizar a etapa na ordem em andamento (nome e
                      instruções para o operador).
                    </p>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => onAddFotoEtapa(etapaIndex)}
                        className="mb-3 text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                        disabled={loading}
                      >
                        <Plus className="h-3 w-3" />
                        Incluir foto parametrizada
                      </button>
                    )}
                    {(!etapa.fotos_etapa || etapa.fotos_etapa.length === 0) ? (
                      <p className="text-sm text-gray-500 italic">Nenhuma foto parametrizada</p>
                    ) : (
                      <div className="space-y-3">
                        {etapa.fotos_etapa.map((foto, fotoIndex) => (
                          <div
                            key={foto._localKeyFoto || foto.id || fotoIndex}
                            className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-600">Nome da foto *</label>
                                <input
                                  type="text"
                                  value={foto.nome}
                                  onChange={(e) =>
                                    onUpdateFotoEtapa(etapaIndex, fotoIndex, 'nome', e.target.value)
                                  }
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="Ex: Painel de controle, Amostra na bancada..."
                                  disabled={isViewMode || loading}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-600">Instruções</label>
                                <textarea
                                  value={foto.instrucoes || ''}
                                  onChange={(e) =>
                                    onUpdateFotoEtapa(etapaIndex, fotoIndex, 'instrucoes', e.target.value)
                                  }
                                  rows={2}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="O que o operador deve registrar na imagem..."
                                  disabled={isViewMode || loading}
                                />
                              </div>
                            </div>
                            {!isViewMode && (
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => onRemoveFotoEtapa(etapaIndex, fotoIndex)}
                                  className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1"
                                  disabled={loading}
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Remover
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 italic">Nenhuma foto obrigatória nesta etapa</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProdutosFinaisPage;

